import db from './database';

/**
 * Extended database utilities to support enhanced database viewer
 */
class DatabaseExtended {
  constructor() {
    this.db = db;
  }

  /**
   * Get the database client instance
   * @param {string} [password] - Optional password to use for connection
   * @returns {Promise<Object>} Database client
   */
  async getClient(password) {
    return this.db.getClient(password);
  }

  /**
   * Execute a SQL query with detailed results and metadata
   * @param {string} sql - SQL query to execute
   * @param {Array} [params=[]] - Query parameters
   * @param {Object} [options={}] - Additional options
   * @returns {Promise<Object>} Detailed query result
   */
  async executeQuery(sql, params = [], options = {}) {
    const startTime = performance.now();
    
    try {
      const result = await this.db.query(sql, params);
      const endTime = performance.now();

      // Extract column names and types from the first row
      let columns = [];
      if (result.rows && result.rows.length > 0) {
        columns = Object.keys(result.rows[0]).map(name => ({
          name,
          // Infer type from the first non-null value
          type: this.inferColumnType(result.rows, name)
        }));
      }

      return {
        success: true,
        rows: result.rows || [],
        columns,
        rowCount: result.rows?.length || 0,
        rowsAffected: result.rowsAffected || 0,
        executionTime: endTime - startTime,
        sql,
        params
      };
    } catch (error) {
      const endTime = performance.now();
      
      return {
        success: false,
        error: error.message,
        executionTime: endTime - startTime,
        sql,
        params
      };
    }
  }

  /**
   * Infer column data type from values
   * @param {Array} rows - Array of data rows
   * @param {string} columnName - Column name to check
   * @returns {string} Inferred SQL data type
   */
  inferColumnType(rows, columnName) {
    // Find the first non-null value
    const nonNullValue = rows.find(row => row[columnName] !== null)?.[columnName];
    
    if (nonNullValue === undefined) {
      return 'UNKNOWN';
    }

    const type = typeof nonNullValue;
    
    switch (type) {
      case 'number':
        return Number.isInteger(nonNullValue) ? 'INTEGER' : 'REAL';
      case 'string':
        // Try to detect dates
        if (/^\d{4}-\d{2}-\d{2}/.test(nonNullValue)) {
          return 'DATETIME';
        }
        return 'TEXT';
      case 'boolean':
        return 'BOOLEAN';
      case 'object':
        if (nonNullValue instanceof Date) {
          return 'DATETIME';
        } else if (nonNullValue instanceof Uint8Array || nonNullValue instanceof ArrayBuffer) {
          return 'BLOB';
        }
        return 'TEXT'; // JSON objects are stored as TEXT
      default:
        return 'TEXT';
    }
  }

  /**
   * Get detailed information about a table
   * @param {string} tableName - Name of the table
   * @returns {Promise<Object>} Table information
   */
  async getTableInfo(tableName) {
    try {
      // Get table structure
      const structure = await this.db.getTableStructure(tableName);
      
      // Get indexes
      const indexesQuery = `
        SELECT 
          name AS index_name,
          "unique" AS is_unique,
          partial AS is_partial,
          origin AS index_type
        FROM pragma_index_list('${tableName}')`;
      const indexesResult = await this.db.query(indexesQuery);
      
      // Get index columns
      const indexes = await Promise.all(
        indexesResult.rows.map(async (index) => {
          const columnsQuery = `
            SELECT
              name AS column_name,
              seqno AS sequence
            FROM pragma_index_info('${index.index_name}')`;
          const columnsResult = await this.db.query(columnsQuery);
          
          return {
            ...index,
            columns: columnsResult.rows,
          };
        })
      );
      
      // Get foreign keys
      const foreignKeysQuery = `
        SELECT 
          id,
          seq,
          "table" AS referenced_table,
          "from" AS column_name,
          "to" AS referenced_column,
          on_update,
          on_delete,
          match AS match_option
        FROM pragma_foreign_key_list('${tableName}')`;
      const foreignKeysResult = await this.db.query(foreignKeysQuery);
      
      // Group foreign keys by ID
      const foreignKeyMap = {};
      for (const fk of foreignKeysResult.rows) {
        if (!foreignKeyMap[fk.id]) {
          foreignKeyMap[fk.id] = {
            id: fk.id,
            referenced_table: fk.referenced_table,
            on_update: fk.on_update,
            on_delete: fk.on_delete,
            match_option: fk.match_option,
            columns: [],
          };
        }
        
        foreignKeyMap[fk.id].columns.push({
          column_name: fk.column_name,
          referenced_column: fk.referenced_column,
          sequence: fk.seq,
        });
      }
      
      const foreignKeys = Object.values(foreignKeyMap);
      
      // Get row count
      const countResult = await this.db.query(`SELECT COUNT(*) as count FROM "${tableName}"`);
      const rowCount = countResult.rows[0].count;
      
      // Get approximate size (SQLite doesn't provide table size directly)
      const sizeEstimate = structure.length * rowCount * 50; // Rough estimate based on column count and row count
      
      return {
        name: tableName,
        structure,
        indexes,
        foreignKeys,
        rowCount,
        sizeEstimate,
        createTime: null, // SQLite doesn't store creation time
        lastModified: null // SQLite doesn't store last modified time
      };
    } catch (error) {
      console.error(`Error getting table info for ${tableName}:`, error);
      throw error;
    }
  }

  /**
   * Get table data with advanced filtering and pagination
   * @param {string} tableName - Name of the table
   * @param {Object} options - Query options
   * @param {number} [options.limit=100] - Maximum number of rows to return
   * @param {number} [options.offset=0] - Number of rows to skip
   * @param {string} [options.orderBy] - Column to order by
   * @param {string} [options.orderDirection='ASC'] - Order direction ('ASC' or 'DESC')
   * @param {Array} [options.filters=[]] - Array of filter objects
   * @returns {Promise<Object>} Table data with metadata
   */
  async getTableData(tableName, {
    limit = 100,
    offset = 0,
    orderBy = null,
    orderDirection = 'ASC',
    filters = []
  } = {}) {
    try {
      // Build query with filters
      let query = `SELECT * FROM "${tableName}"`;
      const params = [];
      
      // Apply filters
      if (filters && filters.length > 0) {
        const enabledFilters = filters.filter(f => f.enabled !== false);
        
        if (enabledFilters.length > 0) {
          const filterClauses = [];
          
          enabledFilters.forEach(filter => {
            const { column, operator, value, value2 } = filter;
            
            // Handle different operators
            switch (operator) {
              case 'IS NULL':
                filterClauses.push(`"${column}" IS NULL`);
                break;
              case 'IS NOT NULL':
                filterClauses.push(`"${column}" IS NOT NULL`);
                break;
              case 'BETWEEN':
                filterClauses.push(`"${column}" BETWEEN ? AND ?`);
                params.push(value);
                params.push(value2);
                break;
              case 'LIKE':
              case 'NOT LIKE':
                // Add wildcards for contains
                filterClauses.push(`"${column}" ${operator} ?`);
                params.push(`%${value}%`);
                break;
              case 'STARTS':
                filterClauses.push(`"${column}" LIKE ?`);
                params.push(`${value}%`);
                break;
              case 'ENDS':
                filterClauses.push(`"${column}" LIKE ?`);
                params.push(`%${value}`);
                break;
              default:
                // Default operators like =, !=, >, <, etc.
                filterClauses.push(`"${column}" ${operator} ?`);
                params.push(value);
                break;
            }
          });
          
          if (filterClauses.length > 0) {
            query += ` WHERE ${filterClauses.join(' AND ')}`;
          }
        }
      }
      
      // Apply ordering
      if (orderBy) {
        query += ` ORDER BY "${orderBy}" ${orderDirection === 'DESC' ? 'DESC' : 'ASC'}`;
      }
      
      // Apply pagination
      query += ` LIMIT ${limit} OFFSET ${offset}`;
      
      // Execute the query
      const startTime = performance.now();
      const result = await this.db.query(query, params);
      const endTime = performance.now();
      
      // Get total row count (without pagination)
      let countQuery = `SELECT COUNT(*) as total FROM "${tableName}"`;
      
      // Apply the same filters to the count query
      if (query.includes('WHERE')) {
        countQuery += ` ${query.substring(query.indexOf('WHERE'), query.indexOf('ORDER BY') > 0 ? query.indexOf('ORDER BY') : query.indexOf('LIMIT'))}`;
      }
      
      const countResult = await this.db.query(countQuery, params.slice(0, params.length - (query.includes('ORDER BY') ? 0 : 0)));
      const totalRows = countResult.rows[0].total;
      
      return {
        success: true,
        rows: result.rows || [],
        totalRows,
        limit,
        offset,
        page: Math.floor(offset / limit) + 1,
        totalPages: Math.ceil(totalRows / limit),
        orderBy,
        orderDirection,
        executionTime: endTime - startTime,
        query,
        params
      };
    } catch (error) {
      console.error(`Error getting table data for ${tableName}:`, error);
      throw error;
    }
  }

  /**
   * Generate CREATE TABLE statement for a table
   * @param {string} tableName - Name of the table
   * @returns {Promise<string>} CREATE TABLE SQL statement
   */
  async generateCreateTableSQL(tableName) {
    try {
      const tableInfo = await this.getTableInfo(tableName);
      const { structure, indexes, foreignKeys } = tableInfo;
      
      let sql = `CREATE TABLE "${tableName}" (\n`;
      
      // Add columns
      const columnDefs = structure.map(column => {
        let columnDef = `  "${column.name}" ${column.type}`;
        
        if (column.pk === 1) {
          columnDef += ' PRIMARY KEY';
          
          if (column.type.toUpperCase() === 'INTEGER' && column.autoincrement === 1) {
            columnDef += ' AUTOINCREMENT';
          }
        }
        
        if (column.notnull === 1) {
          columnDef += ' NOT NULL';
        }
        
        if (column.dflt_value !== null) {
          columnDef += ` DEFAULT ${column.dflt_value}`;
        }
        
        return columnDef;
      });
      
      // Add foreign key constraints
      const fkConstraints = foreignKeys.map(fk => {
        const columnNames = fk.columns.map(col => `"${col.column_name}"`).join(', ');
        const refColumnNames = fk.columns.map(col => `"${col.referenced_column}"`).join(', ');
        
        let constraint = `  FOREIGN KEY (${columnNames}) REFERENCES "${fk.referenced_table}" (${refColumnNames})`;
        
        if (fk.on_delete !== 'NO ACTION') {
          constraint += ` ON DELETE ${fk.on_delete}`;
        }
        
        if (fk.on_update !== 'NO ACTION') {
          constraint += ` ON UPDATE ${fk.on_update}`;
        }
        
        return constraint;
      });
      
      // Combine all parts of the CREATE TABLE statement
      sql += [...columnDefs, ...fkConstraints].join(',\n');
      sql += '\n);\n';
      
      // Add index creation statements
      indexes.forEach(index => {
        if (index.index_name.startsWith(`sqlite_autoindex_${tableName}`)) {
          return; // Skip auto-generated indexes
        }
        
        const columnDefs = index.columns.map(col => {
          return `"${col.column_name}"`;
        }).join(', ');
        
        sql += `\nCREATE ${index.is_unique ? 'UNIQUE ' : ''}INDEX "${index.index_name}" ON "${tableName}" (${columnDefs});`;
      });
      
      return sql;
    } catch (error) {
      console.error(`Error generating CREATE TABLE SQL for ${tableName}:`, error);
      throw error;
    }
  }

  /**
   * Get database statistics
   * @returns {Promise<Object>} Database statistics
   */
  async getDatabaseStats() {
    try {
      // Get list of all tables
      const tables = await this.db.getTables();
      
      // Get information for each table
      const tableStats = await Promise.all(
        tables.map(async (tableName) => {
          try {
            const countResult = await this.db.query(`SELECT COUNT(*) as count FROM "${tableName}"`);
            const rowCount = countResult.rows[0].count;
            
            const structureResult = await this.db.getTableStructure(tableName);
            const columnCount = structureResult.length;
            
            return {
              name: tableName,
              rowCount,
              columnCount
            };
          } catch (error) {
            console.error(`Error getting stats for table ${tableName}:`, error);
            return {
              name: tableName,
              rowCount: 0,
              columnCount: 0,
              error: error.message
            };
          }
        })
      );
      
      // Get total row count across all tables
      const totalRows = tableStats.reduce((sum, table) => sum + table.rowCount, 0);
      
      // Get database file size (not available in web environment)
      const dbSize = 'N/A';
      
      return {
        tables: tableStats,
        tableCount: tables.length,
        totalRows,
        dbSize,
        // SQLite version
        version: await this.getSQLiteVersion()
      };
    } catch (error) {
      console.error('Error getting database stats:', error);
      throw error;
    }
  }

  /**
   * Get SQLite version
   * @returns {Promise<string>} SQLite version
   */
  async getSQLiteVersion() {
    try {
      const result = await this.db.query('SELECT sqlite_version() as version');
      return result.rows[0].version;
    } catch (error) {
      console.error('Error getting SQLite version:', error);
      return 'Unknown';
    }
  }

  /**
   * Check if a table exists
   * @param {string} tableName - Name of the table to check
   * @returns {Promise<boolean>} Whether the table exists
   */
  async tableExists(tableName) {
    try {
      const result = await this.db.query(
        "SELECT name FROM sqlite_master WHERE type='table' AND name=?",
        [tableName]
      );
      return result.rows.length > 0;
    } catch (error) {
      console.error(`Error checking if table ${tableName} exists:`, error);
      return false;
    }
  }

  /**
   * Export table data to various formats
   * @param {string} tableName - Name of the table to export
   * @param {string} format - Export format ('json', 'csv', 'sql', 'markdown')
   * @param {Object} options - Export options
   * @returns {Promise<string>} Exported data
   */
  async exportTable(tableName, format = 'json', options = {}) {
    try {
      // Get table data
      const result = await this.db.query(`SELECT * FROM "${tableName}"`);
      const rows = result.rows || [];
      
      // Get table structure for SQL export
      const structure = await this.db.getTableStructure(tableName);
      
      // Handle different export formats
      switch (format.toLowerCase()) {
        case 'json':
          return JSON.stringify(rows, null, 2);
          
        case 'csv': {
          if (rows.length === 0) return '';
          
          const headers = Object.keys(rows[0]);
          const headerRow = headers.map(header => `"${header}"`).join(',');
          
          const dataRows = rows.map(row => {
            return headers.map(header => {
              const value = row[header];
              if (value === null) return '';
              if (typeof value === 'string') return `"${value.replace(/"/g, '""')}"`;
              return value;
            }).join(',');
          });
          
          return [headerRow, ...dataRows].join('\n');
        }
          
        case 'sql': {
          // Generate INSERT statements
          let sql = `-- Table: ${tableName}\n`;
          
          // Add CREATE TABLE statement if requested
          if (options.includeSchema) {
            sql += await this.generateCreateTableSQL(tableName);
            sql += '\n\n';
          }
          
          // Add INSERT statements
          if (rows.length > 0) {
            const headers = Object.keys(rows[0]);
            
            rows.forEach(row => {
              const values = headers.map(header => {
                const value = row[header];
                if (value === null) return 'NULL';
                if (typeof value === 'string') return `'${value.replace(/'/g, "''")}'`;
                return value;
              });
              
              sql += `INSERT INTO "${tableName}" (${headers.map(h => `"${h}"`).join(', ')}) VALUES (${values.join(', ')});\n`;
            });
          }
          
          return sql;
        }
          
        case 'markdown': {
          if (rows.length === 0) return `# Table: ${tableName}\n\nNo data`;
          
          const headers = Object.keys(rows[0]);
          const headerRow = `| ${headers.join(' | ')} |`;
          const separatorRow = `| ${headers.map(() => '---').join(' | ')} |`;
          
          const dataRows = rows.map(row => {
            return `| ${headers.map(header => {
              const value = row[header];
              return value === null ? '' : String(value);
            }).join(' | ')} |`;
          });
          
          return [`# Table: ${tableName}`, '', headerRow, separatorRow, ...dataRows].join('\n');
        }
          
        default:
          throw new Error(`Unsupported export format: ${format}`);
      }
    } catch (error) {
      console.error(`Error exporting table ${tableName} to ${format}:`, error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const dbExtended = new DatabaseExtended();
export default dbExtended;